// src/db.ts
import Dexie, { type Table } from 'dexie'

// Define interfaces for each store's records.
export interface Kanji {
  kanji: string
  translation?: string
  bushu?: string
}

export interface Bushu {
  bushu?: string
  hiragana?: string
}

export interface Reading {
  id: string // id is a combination of kanji and reading
  kanji: string
  readingType: 'on' | 'kun'
  reading: string
}

export interface Word {
  id: string // id is a combination of word and furigana
  word: string
  furigana: string
  translation: string
  kanjiSet: string[]
}

export interface UserKanji {
  kanji: string
  shouldShow: boolean
  tags: string[]
  createdAt: Date
}

export interface SRSData {
  id: string
  type: 'word' | 'reading'
  lastReviewedAt?: Date
  nextReviewAt: Date
  interval?: number
  repetitionCount?: number
  easeFactor?: number
  reviewHistory?: Array<{
    createdAt: Date
    rating: string
    intervalBeforeReview: number
  }>
}

export interface SearchHistory {
  kanji: string
  createdAt: Date
}

export interface Exercise {
  id: string
  name: string
  mode:
    | 'reading-flashcards'
    | 'word-flashcards'
    | 'mixed-flashcards'
    | 'word-quiz'
    | 'word-to-reading-quiz'
    | 'reading-to-word-quiz'
  tags: string[]
  revision: number
  createdAt: Date
}

export interface StudySession {
  id: string
  exerciseId: string
  revision: number
  startedAt: Date
  endedAt?: Date

  totalReviewed?: number
  againCount?: number
  hardCount?: number
  goodCount?: number
  easyCount?: number
}

export interface StudyAttempt {
  id: string
  sessionId: string
  cardId: string
  cardType: 'word' | 'reading'
  rating: 0 | 1 | 2 | 3
  createdAt: Date
  elapsedMs?: number
}

// Create a Dexie subclass for your database.
export class KanjiLearningDB extends Dexie {
  // Imported data stores
  kanjis!: Table<Kanji, string>
  bushus!: Table<Bushu, string>
  readings!: Table<Reading, string>
  words!: Table<Word, string>

  // User‑related data stores
  userKanjis!: Table<UserKanji, string>
  srsData!: Table<SRSData, string>
  searchHistory!: Table<SearchHistory, string>
  exercises!: Table<Exercise, string>
  studySessions!: Table<StudySession, string>
  studyAttempts!: Table<StudyAttempt, string>

  constructor() {
    super('KanjiLearningDB')
    this.version(1).stores({
      kanjis: 'kanji',
      bushus: 'bushu',
      readings: 'id, kanji, readingType',
      words: 'id, word, furigana, translation, *kanjiSet',
      userKanjis: 'kanji, shouldShow',
      srsData: 'id, nextReviewAt',
    })
    this.version(2)
      .stores({
        kanjis: 'kanji',
        bushus: 'bushu',
        readings: 'id, kanji, readingType',
        words: 'id, word, furigana, translation, *kanjiSet',
        userKanjis: 'kanji, shouldShow',
        srsData: 'id, nextReviewAt',
      })
      .upgrade(async (trans) => {
        const readingsStore = trans.table('readings')
        await readingsStore.toCollection().modify((record) => {
          record.reading = record.katakana
          delete record.katakana
        })
      })
    this.version(3)
      .stores({
        kanjis: 'kanji',
        bushus: 'bushu',
        readings: 'id, kanji, readingType',
        words: 'id, word, furigana, translation, *kanjiSet',
        userKanjis: 'kanji, shouldShow',
        srsData: 'id, type, nextReviewAt',
      })
      .upgrade(async (trans) => {
        const srsDataStore = trans.table('srsData')
        await srsDataStore.toCollection().modify((record) => {
          record.type = 'word'
        })
      })
    this.version(4).stores({
      kanjis: 'kanji',
      bushus: 'bushu',
      readings: 'id, kanji, readingType',
      words: 'id, word, furigana, translation, *kanjiSet',
      userKanjis: 'kanji, shouldShow',
      srsData: 'id, type, nextReviewAt',
      searchHistory: 'kanji, createdAt',
    })
    this.version(5)
      .stores({
        kanjis: 'kanji, *tags',
        bushus: 'bushu',
        readings: 'id, kanji, readingType',
        words: 'id, word, furigana, translation, *kanjiSet',
        userKanjis: 'kanji, shouldShow',
        srsData: 'id, type, nextReviewAt',
        searchHistory: 'kanji, createdAt',
      })
      .upgrade(async (trans) => {
        const kanjisStore = trans.table('kanjis')
        await kanjisStore.toCollection().modify((record) => {
          record.tags = []
        })
      })
    this.version(6)
      .stores({
        kanjis: 'kanji',
        bushus: 'bushu',
        readings: 'id, kanji, readingType',
        words: 'id, word, furigana, translation, *kanjiSet',
        userKanjis: 'kanji, shouldShow, *tags',
        srsData: 'id, type, nextReviewAt',
        searchHistory: 'kanji, createdAt',
      })
      .upgrade(async (trans) => {
        const userKanjis = trans.table('userKanjis')
        await userKanjis.toCollection().modify((record) => {
          record.tags = []
        })
      })
    this.version(7).stores({
      kanjis: 'kanji',
      bushus: 'bushu',
      readings: 'id, kanji, readingType',
      words: 'id, word, furigana, translation, *kanjiSet',
      userKanjis: 'kanji, shouldShow, *tags',
      srsData: 'id, type, nextReviewAt',
      searchHistory: 'kanji, createdAt',
      exercises: 'id, name, mode, *tags, createdAt',
      studySessions: 'id, exerciseId, startedAt, endedAt',
      studyAttempts: 'id, sessionId, cardId, cardType, createdAt',
    })
    this.version(8)
      .stores({
        kanjis: 'kanji',
        bushus: 'bushu',
        readings: 'id, kanji, readingType',
        words: 'id, word, furigana, translation, *kanjiSet',
        userKanjis: 'kanji, shouldShow, *tags',
        srsData: 'id, type, nextReviewAt',
        searchHistory: 'kanji, createdAt',
        exercises: 'id, name, mode, *tags, createdAt',
        studySessions: 'id, exerciseId, startedAt, endedAt',
        studyAttempts: 'id, sessionId, cardId, cardType, createdAt',
      })
      .upgrade(async (trans) => {
        // Migrate old exercise mode values to new ones with 'flashcard' in the name
        const exercisesTable = trans.table('exercises')
        const allExercises = await exercisesTable.toArray()

        for (const exercise of allExercises) {
          let needsUpdate = false
          let updatedMode = exercise.mode

          // Map old values to new values
          switch (exercise.mode) {
            case 'readings':
              updatedMode = 'reading-flashcards'
              needsUpdate = true
              break
            case 'words':
              updatedMode = 'word-flashcards'
              needsUpdate = true
              break
            case 'both':
              updatedMode = 'mixed-flashcards'
              needsUpdate = true
              break
          }

          if (needsUpdate) {
            await exercisesTable.update(exercise.id, { mode: updatedMode })
          }
        }
      })
    const userKanjisStore = this.table('userKanjis')
    userKanjisStore.toCollection().modify((record) => {
      if (!record.tags) {
        record.tags = []
      }
    })
  }
}

const db = new KanjiLearningDB()

if (process.env.NODE_ENV === 'developmenta') {
  // Loop over every table in your database:
  db.tables.forEach((table) => {
    // Log every read operation:
    table.hook('reading', (obj) => {
      console.log(`[DEBUG] Reading from ${table.name}:`, obj)
      // Return the object unmodified.
      return obj
    })

    // Optionally, hook into other operations, for example, creating:
    table.hook('creating', (primKey, obj, transaction) => {
      console.log(`[DEBUG] Creating in ${table.name} with key:`, primKey, obj)
      return obj
    })

    // And updating:
    table.hook('updating', (modifications, primKey, obj, transaction) => {
      console.log(`[DEBUG] Updating in ${table.name} on key:`, primKey, modifications)
      return modifications
    })

    // And deleting:
    table.hook('deleting', (primKey, obj, transaction) => {
      console.log(`[DEBUG] Deleting from ${table.name} with key:`, primKey)
    })
  })
}

export default db
